/**
 * React Hook for Job Media Management
 * Provides easy access to job media upload, retrieval, update, and delete operations
 * 
 * Usage:
 * ```tsx
 * const { media, loading, upload, fetch, delete: deleteMedia } = useJobMedia({
 *   collectionId: 'YOUR_COLLECTION_ID',
 *   jobId: 'job-123'
 * });
 * ```
 */

import React, { useState, useCallback, useEffect, createContext, ReactNode } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import type { JobMediaItem } from '@rsmaids/shared/types/jobMedia';
import i18n from '../i18n';

const UPLOAD_MEDIA_URL = 'https://uploadjobmediahttp-3ramtd76aa-uc.a.run.app';

interface UseJobMediaOptions {
  collectionId: string;
  jobId: string;
  autoFetch?: boolean; // Automatically fetch media on mount
}


interface BulkUploadResponseData {
  media: JobMediaItem[];
}

interface FetchResponse {
  items?: JobMediaItem[];
}

interface StatsResponse {
  totalCount: number;
  byType: Record<string, number>;
  byMimeType: Record<string, number>;
  recentlyAdded: JobMediaItem[];
}

interface UseJobMediaOptions {
  collectionId: string;
  jobId: string;
  autoFetch?: boolean; // Automatically fetch media on mount
}

interface UseJobMediaReturn {
  media: JobMediaItem[];
  loading: boolean;
  error: string | null;
  upload: (file: File, metadata?: { description?: string; tags?: string[] }, onCompressed?: (size: number) => void) => Promise<JobMediaItem>;
  bulkUpload: (files: File[]) => Promise<JobMediaItem[]>;
  fetch: () => Promise<void>;
  delete: (mediaItemId: string) => Promise<void>;
  update: (mediaItemId: string, updateData: Partial<JobMediaItem>) => Promise<JobMediaItem>;
  reorder: (newOrder: Array<{ mediaItemId: string; displayOrder: number }>) => Promise<void>;
  getStats: () => Promise<StatsResponse>;
  clearError: () => void;
}

/**
 * Hook for managing job media
 */
export function useJobMedia(options: UseJobMediaOptions): UseJobMediaReturn {
  const { collectionId, jobId, autoFetch = true } = options;
  const [media, setMedia] = useState<JobMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Helper to call Wix Admin functions
   */
  const callHandler = useCallback(
    async (method: string, params: Record<string, unknown> = {}): Promise<unknown> => {
      try {
        const functions = getFunctions();
        const uploadMedia = httpsCallable(functions, 'wixAdmin');
        const response = await uploadMedia({
          collection: 'jobMedia',
          method,
          params: {
            dataCollectionId: collectionId,
            jobId,
            ...params
          }
        });
        return response.data;
      } catch (err) {
        throw err;
      }
    },
    [collectionId, jobId]
  );

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  /**
   * Compress an image file using Canvas before upload.
   * Resizes to max 1920px on the longest side and encodes as JPEG at 85% quality.
   * Non-image files and already-small images are returned unchanged.
   */
  const compressImage = (file: File, maxDimension = 1920, quality = 0.85): Promise<File> => {
    if (!file.type.startsWith('image/')) return Promise.resolve(file);

    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        let { width, height } = img;

        // Skip compression if already within limits
        if (width <= maxDimension && height <= maxDimension) {
          resolve(file);
          return;
        }

        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
      img.src = objectUrl;
    });
  };

  /**
   * Upload a single file
   */
  const upload = useCallback(
    async (file: File, metadata?: { description?: string; tags?: string[] }, onCompressed?: (size: number) => void) => {
      setLoading(true);
      setError(null);
      try {
        // Get Firebase ID token to authenticate the HTTP function
        const idToken = await getAuth().currentUser?.getIdToken();
        if (!idToken) throw new Error('Not authenticated');

        // Compress images before upload (videos are already compressed by the native plugin)
        const uploadFile = await compressImage(file);
        onCompressed?.(uploadFile.size);

        // Read into memory first — mobile browsers fail to stream File objects
        // from the file system directly as fetch bodies (same as native which reads blob first)
        const fileBuffer = await uploadFile.arrayBuffer();

        // POST raw binary to Firebase HTTP function — no base64, no memory spike
        const uploadResponse = await globalThis.fetch(UPLOAD_MEDIA_URL, {
          method: 'POST',
          headers: {
            'Content-Type': uploadFile.type || 'application/octet-stream',
            'Authorization': `Bearer ${idToken}`,
            'X-File-Name': encodeURIComponent(uploadFile.name),
          },
          body: fileBuffer,
        });
        if (!uploadResponse.ok) {
          const err = await uploadResponse.json().catch(() => ({})) as { error?: string };
          throw new Error(err.error ?? `Upload failed: ${uploadResponse.status}`);
        }
        const { fileUrl, uploadedAt, fileSize } = await uploadResponse.json() as { fileUrl: string; uploadedAt: string; fileSize: number };
        if (!fileUrl) throw new Error('No file URL in upload response');

        // Save metadata to CMS (tiny request)
        const mediaItem = await callHandler('createJobMediaItem', {
          mediaData: {
            jobId: jobId,
            fileUrl,
            fileName: uploadFile.name,
            mimeType: uploadFile.type,
            fileSize: fileSize ?? uploadFile.size,
            uploadedAt: uploadedAt ?? new Date().toISOString(),
            displayOrder: media.length,
            ...metadata
          }
        });

        const result = { ...(mediaItem as JobMediaItem), fileSize: fileSize ?? uploadFile.size };
        setMedia(prev => [...prev, result]);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.uploadFailed');
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [callHandler, media.length, jobId]
  );

  /**
   * Upload multiple files at once
   */
  const bulkUpload = useCallback(
    async (files: File[]) => {
      setLoading(true);
      setError(null);
      try {
        const mediaFiles = [];

        for (const file of files) {
          const base64Data = await fileToBase64(file);
          mediaFiles.push({
            base64Data,
            fileName: file.name,
            mimeType: file.type
          });
        }

        const result = await callHandler('bulkUploadJobMedia', {
          mediaFiles
        });

        setMedia(prev => [...prev, ...(result as BulkUploadResponseData).media]);
        return (result as BulkUploadResponseData).media;
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.bulkUploadFailed');
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [callHandler]
  );

  /**
   * Fetch all media for the job
   */
  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callHandler('getJobMedia');
      setMedia(Array.isArray(result) ? result : (result as FetchResponse)?.items || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.failedToFetchMedia');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [callHandler]);

  /**
   * Delete a media item
   */
  const deleteMedia = useCallback(
    async (mediaItemId: string) => {
      setError(null);
      try {
        await callHandler('deleteJobMediaItem', { mediaItemId });
        setMedia(prev => prev.filter(m => m._id !== mediaItemId));
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.deleteFailed');
        setError(message);
        throw err;
      }
    },
    [callHandler]
  );

  /**
   * Update media metadata
   */
  const update = useCallback(
    async (mediaItemId: string, updateData: Partial<JobMediaItem>) => {
      setError(null);
      try {
        const updated = await callHandler('updateJobMediaItem', {
          mediaItemId,
          updateData
        });
        setMedia(prev =>
          prev.map(m => (m._id === mediaItemId ? (updated as JobMediaItem) : m))
        );
        return updated as JobMediaItem;
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.updateFailed');
        setError(message);
        throw err;
      }
    },
    [callHandler]
  );

  /**
   * Reorder media items
   */
  const reorder = useCallback(
    async (newOrder: Array<{ mediaItemId: string; displayOrder: number }>) => {
      setError(null);
      try {
        await callHandler('reorderJobMedia', { mediaOrder: newOrder });
        // Re-fetch to get updated order
        await fetch();
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.reorderFailed');
        setError(message);
        throw err;
      }
    },
    [callHandler, fetch]
  );

  /**
   * Get media statistics
   */
  const getStats = useCallback(async (): Promise<StatsResponse> => {
    setError(null);
    try {
      return await callHandler('getJobMediaStats') as StatsResponse;
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n.t('hooks.useJobMedia.failedToGetStats');
      setError(message);
      throw err;
    }
  }, [callHandler]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Auto-fetch media on mount or when jobId changes
   */
  useEffect(() => {
    if (autoFetch && jobId) {
      Promise.resolve().then(() => fetch());
    }
  }, [autoFetch, jobId, fetch]);

  return {
    media,
    loading,
    error,
    upload,
    bulkUpload,
    fetch,
    delete: deleteMedia,
    update,
    reorder,
    getStats,
    clearError
  };
}

/**
 * Higher-order component version of useJobMedia for class components
 */
export function withJobMedia<P extends { jobMedia?: ReturnType<typeof useJobMedia> }>(
  Component: React.ComponentType<P>,
  options: UseJobMediaOptions
) {
  return function JobMediaComponent(props: Omit<P, 'jobMedia'>) {
    const jobMedia = useJobMedia(options);
    return React.createElement(Component, { ...props, jobMedia } as P);
  };
}

/**
 * Context for sharing job media across components
 */
interface JobMediaContextType {
  collectionId: string;
  jobId: string;
}

export const JobMediaContext = createContext<JobMediaContextType | null>(null);

interface JobMediaProviderProps {
  collectionId: string;
  jobId: string;
  children: ReactNode;
}

export function JobMediaProvider({
  collectionId,
  jobId,
  children
}: JobMediaProviderProps) {
  return React.createElement(
    JobMediaContext.Provider,
    { value: { collectionId, jobId } },
    children
  );
}

/**
 * Hook to use JobMediaContext
 */
export function useJobMediaContext() {
  const context = React.useContext(JobMediaContext);
  if (!context) {
    throw new Error('useJobMediaContext must be used within JobMediaProvider');
  }
  return context;
}
